import { useState, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { useTheme } from '../context/ThemeContext.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { brandkits as bkApi, fonts as fontsApi } from '../api/client.js';

export default function BrandKitsPanel({ onClose, onSelectFont }) {
  const { theme } = useTheme();
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'editor';

  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKit, setSelectedKit] = useState(null);
  const [kitDetail, setKitDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [newClientId, setNewClientId] = useState('');
  const [newClientName, setNewClientName] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [creating, setCreating] = useState(false);

  // Add font
  const [showAddFont, setShowAddFont] = useState(false);
  const [fontSearch, setFontSearch] = useState('');
  const [fontResults, setFontResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [usageNote, setUsageNote] = useState('');

  // Edit note
  const [editingFontId, setEditingFontId] = useState(null);
  const [editNote, setEditNote] = useState('');

  const fetchKits = () => {
    setLoading(true);
    bkApi.list()
      .then(data => setKits(data.kits || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const fetchKitDetail = (id) => {
    setDetailLoading(true);
    bkApi.get(id)
      .then(data => setKitDetail(data.kit || null))
      .catch(console.error)
      .finally(() => setDetailLoading(false));
  };

  useEffect(() => { fetchKits(); }, []);

  useEffect(() => {
    if (selectedKit) fetchKitDetail(selectedKit);
    else setKitDetail(null);
  }, [selectedKit]);

  const handleCreate = async () => {
    if (!newClientId.trim() || !newClientName.trim()) return;
    setCreating(true);
    try {
      await bkApi.create({
        client_id: newClientId.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-'),
        client_name: newClientName.trim(),
        notes: newNotes.trim() || undefined,
      });
      setNewClientId('');
      setNewClientName('');
      setNewNotes('');
      setShowCreate(false);
      fetchKits();
    } catch (err) {
      alert(err.message || 'Failed to create brand kit');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this brand kit? All font associations will be removed.')) return;
    try {
      await bkApi.remove(id);
      if (selectedKit === id) { setSelectedKit(null); setKitDetail(null); }
      fetchKits();
    } catch (err) {
      alert(err.message || 'Failed to delete');
    }
  };

  const handleSearchFonts = async (q) => {
    setFontSearch(q);
    if (q.length < 2) { setFontResults([]); return; }
    setSearchLoading(true);
    try {
      const data = await fontsApi.list({ search: q, limit: 20 });
      setFontResults(data.fonts || []);
    } catch { setFontResults([]); }
    finally { setSearchLoading(false); }
  };

  const handleAddFont = async (fontId) => {
    if (!selectedKit) return;
    try {
      await bkApi.addFonts(selectedKit, [fontId], usageNote || undefined);
      fetchKitDetail(selectedKit);
      fetchKits();
    } catch (err) {
      alert(err.message || 'Failed to add font');
    }
  };

  const handleRemoveFont = async (fontId) => {
    if (!selectedKit) return;
    try {
      await bkApi.removeFont(selectedKit, fontId);
      fetchKitDetail(selectedKit);
      fetchKits();
    } catch (err) {
      alert(err.message || 'Failed to remove font');
    }
  };

  const handleUpdateNote = async (fontId) => {
    if (!selectedKit) return;
    try {
      await bkApi.updateFontNote(selectedKit, fontId, editNote);
      setEditingFontId(null);
      setEditNote('');
      fetchKitDetail(selectedKit);
    } catch (err) {
      alert(err.message || 'Failed to update note');
    }
  };

  const btnStyle = (primary) => ({
    padding: primary ? '8px 16px' : '6px 12px',
    background: primary ? theme.fill : 'transparent',
    border: primary ? 'none' : `1px solid ${theme.borderMedium}`,
    borderRadius: 0,
    color: primary ? theme.fillFg : theme.textSecondary,
    fontSize: 12, textTransform: 'lowercase', cursor: 'pointer',
    fontFamily: 'inherit',
  });

  const inputStyle: CSSProperties = {
    width: '100%', height: 38, padding: '0 12px', boxSizing: 'border-box',
    background: 'transparent', border: `1px solid ${theme.borderMedium}`,
    borderRadius: 0, color: theme.text, fontSize: 13,
    outline: 'none', fontFamily: 'inherit',
  };

  const usageColors = {
    'Headline': theme.accent,
    'Body': theme.green,
    'Display': theme.purple,
    'Caption': theme.orange,
    'Code': theme.textSecondary,
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: theme.overlay,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '90%', maxWidth: 960, maxHeight: '85vh',
          background: theme.bgElevated, borderRadius: 0,
          border: `2px solid ${theme.text}`, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 24px', borderBottom: `2px solid ${theme.text}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>&#9830;</span>
            <div>
              <div style={{ fontFamily: "'Aeonik', Arial, Helvetica, sans-serif", fontWeight: 450, fontSize: 18, color: theme.text }}>brand kits</div>
              <div style={{ fontSize: 11, color: theme.textFaint }}>
                curate font collections for each client
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {canEdit && !selectedKit && (
              <button onClick={() => setShowCreate(!showCreate)} style={btnStyle(true)}>
                + new brand kit
              </button>
            )}
            {selectedKit && (
              <button onClick={() => { setSelectedKit(null); setShowAddFont(false); }} style={btnStyle(false)}>
                &larr; all kits
              </button>
            )}
            <span onClick={onClose} style={{ fontSize: 13, color: theme.textFaint, cursor: 'pointer', padding: '4px 8px', lineHeight: 1 }}>&times;</span>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>

          {/* Create form */}
          {showCreate && !selectedKit && (
            <div style={{
              background: 'transparent', borderRadius: 0,
              border: `1px solid ${theme.border}`, padding: 20, marginBottom: 20,
            }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: theme.text, marginBottom: 12 }}>
                create brand kit
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, display: 'block' }}>Client Name</label>
                  <input
                    value={newClientName}
                    onChange={e => {
                      setNewClientName(e.target.value);
                      if (!newClientId || newClientId === newClientName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')) {
                        setNewClientId(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
                      }
                    }}
                    placeholder="e.g. GoGreen Financing"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, display: 'block' }}>Client ID (slug)</label>
                  <input
                    value={newClientId}
                    onChange={e => setNewClientId(e.target.value)}
                    placeholder="e.g. gogreen-financing"
                    style={{ ...inputStyle, fontFamily: "'IBM Plex Mono', monospace" }}
                  />
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, display: 'block' }}>Notes (optional)</label>
                <input
                  value={newNotes}
                  onChange={e => setNewNotes(e.target.value)}
                  placeholder="Brand guidelines link, preferences, etc."
                  style={inputStyle}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => { setShowCreate(false); setNewClientId(''); setNewClientName(''); setNewNotes(''); }} style={btnStyle(false)}>cancel</button>
                <button onClick={handleCreate} disabled={creating || !newClientId || !newClientName} style={{ ...btnStyle(true), opacity: (!newClientId || !newClientName) ? 0.5 : 1 }}>
                  {creating ? 'creating…' : 'create kit'}
                </button>
              </div>
            </div>
          )}

          {/* Kit list */}
          {!selectedKit && (
            loading ? (
              <div style={{ textAlign: 'center', padding: 40, color: theme.textMuted, fontSize: 13 }}>loading brand kits…</div>
            ) : kits.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60, color: theme.textMuted }}>
                <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }}>&#9830;</div>
                <div style={{ fontSize: 14 }}>no brand kits yet</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>create one to start curating client font collections</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
                {kits.map(kit => (
                  <div
                    key={kit.id}
                    onClick={() => setSelectedKit(kit.id)}
                    style={{
                      background: 'transparent', borderRadius: 0,
                      border: `1px solid ${theme.border}`,
                      padding: 16, cursor: 'pointer',
                      transition: 'border-color 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = theme.accent; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>{kit.client_name}</div>
                        <div style={{ fontSize: 11, color: theme.textMuted, fontFamily: "'IBM Plex Mono', monospace" }}>{kit.client_id}</div>
                      </div>
                      {canEdit && (
                        <button
                          onClick={e => { e.stopPropagation(); handleDelete(kit.id); }}
                          style={{ background: 'none', border: 'none', color: theme.textDisabled, cursor: 'pointer', fontSize: 14, padding: '2px 4px' }}
                          title="Delete kit"
                        >&times;</button>
                      )}
                    </div>
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '4px 10px', borderRadius: 0, border: `1px solid ${kit.font_count > 0 ? theme.accent : theme.border}`,
                      color: kit.font_count > 0 ? theme.accent : theme.textMuted,
                      fontSize: 11, fontWeight: 600,
                    }}>
                      <span style={{ fontSize: 13 }}>&#120299;</span>
                      {kit.font_count} font{kit.font_count !== 1 ? 's' : ''}
                    </div>
                    {kit.notes && (
                      <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 8, lineHeight: 1.5 }}>{kit.notes}</div>
                    )}
                  </div>
                ))}
              </div>
            )
          )}

          {/* Kit detail */}
          {selectedKit && (
            detailLoading ? (
              <div style={{ textAlign: 'center', padding: 40, color: theme.textMuted, fontSize: 13 }}>loading…</div>
            ) : kitDetail ? (
              <div>
                {/* Kit header */}
                <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', marginBottom: 20 }}>
                  <div>
                    <div style={{ fontFamily: "'Aeonik', Arial, Helvetica, sans-serif", fontWeight: 450, fontSize: 24, color: theme.text }}>{kitDetail.client_name}</div>
                    <div style={{ fontSize: 12, color: theme.textFaint, fontFamily: "'IBM Plex Mono', monospace", marginTop: 2 }}>{kitDetail.client_id}</div>
                    {kitDetail.notes && <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 6 }}>{kitDetail.notes}</div>}
                  </div>
                  {canEdit && (
                    <button onClick={() => setShowAddFont(!showAddFont)} style={btnStyle(true)}>
                      {showAddFont ? 'done adding' : '+ add fonts'}
                    </button>
                  )}
                </div>

                {/* Add font search */}
                {showAddFont && (
                  <div style={{
                    background: 'transparent', borderRadius: 0,
                    border: `1px solid ${theme.border}`, padding: 16, marginBottom: 20,
                  }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <input
                        value={fontSearch}
                        onChange={e => handleSearchFonts(e.target.value)}
                        placeholder="search fonts to add…"
                        style={{ ...inputStyle, flex: 1 }}
                        autoFocus
                      />
                      <select
                        value={usageNote}
                        onChange={e => setUsageNote(e.target.value)}
                        style={{ ...inputStyle, width: 140 }}
                      >
                        <option value="">Usage (optional)</option>
                        <option value="Headline">Headline</option>
                        <option value="Body">Body</option>
                        <option value="Display">Display</option>
                        <option value="Caption">Caption</option>
                        <option value="Code">Code</option>
                      </select>
                    </div>
                    {searchLoading && <div style={{ fontSize: 11, color: theme.textMuted, padding: 8 }}>searching…</div>}
                    {fontResults.length > 0 && (
                      <div style={{ maxHeight: 200, overflow: 'auto' }}>
                        {fontResults.map(f => {
                          const alreadyIn = (kitDetail.fonts || []).some(kf => kf.id === f.id);
                          return (
                            <div key={f.id} style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '8px 10px', borderRadius: 0,
                              background: alreadyIn ? theme.bgTertiary : 'transparent',
                            }}>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>{f.family || f.name}</div>
                                <div style={{ fontSize: 10, color: theme.textMuted }}>
                                  {f.category} &middot; {f.style_count} style{f.style_count !== 1 ? 's' : ''} &middot; {f.source}
                                </div>
                              </div>
                              {alreadyIn ? (
                                <span style={{ fontSize: 11, color: theme.textMuted, fontWeight: 600 }}>added</span>
                              ) : (
                                <button onClick={() => handleAddFont(f.id)} style={{
                                  ...btnStyle(false), padding: '4px 10px', fontSize: 11, color: theme.accent,
                                }}>+ add</button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {fontSearch.length >= 2 && !searchLoading && fontResults.length === 0 && (
                      <div style={{ fontSize: 11, color: theme.textMuted, padding: 8 }}>no fonts found</div>
                    )}
                  </div>
                )}

                {/* Font list */}
                {(kitDetail.fonts || []).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: theme.textMuted }}>
                    <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.3 }}>&#120299;</div>
                    <div style={{ fontSize: 13 }}>no fonts in this kit yet</div>
                    <div style={{ fontSize: 11, marginTop: 4 }}>click "add fonts" to search and add fonts</div>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: 8 }}>
                    {(kitDetail.fonts || []).map(f => (
                      <div key={f.id} style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '12px 16px', borderRadius: 0,
                        background: 'transparent', border: `1px solid ${theme.border}`,
                      }}>
                        {/* Font preview */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                            <span
                              style={{ fontSize: 14, fontWeight: 700, color: theme.text, cursor: 'pointer' }}
                              onClick={() => { onSelectFont?.(f.id); onClose(); }}
                              title="View font detail"
                            >{f.family || f.name}</span>
                            {f.usage_note && (
                              <span style={{
                                fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 0,
                                border: `1px solid ${usageColors[f.usage_note] || theme.textFaint}`,
                                color: usageColors[f.usage_note] || theme.textFaint,
                              }}>{f.usage_note}</span>
                            )}
                            {f.is_variable ? (
                              <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 0, border: `1px solid ${theme.purple}`, color: theme.purple }}>VAR</span>
                            ) : null}
                          </div>
                          <div style={{ fontSize: 10, color: theme.textMuted }}>
                            {f.category} &middot; {f.style_count} style{f.style_count !== 1 ? 's' : ''} &middot; {f.size_display || ''} &middot; {f.source}
                            {f.license_type && <> &middot; {f.license_type}</>}
                          </div>
                        </div>

                        {/* Actions */}
                        {canEdit && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                            {editingFontId === f.id ? (
                              <div style={{ display: 'flex', gap: 4 }}>
                                <select
                                  value={editNote}
                                  onChange={e => setEditNote(e.target.value)}
                                  style={{ ...inputStyle, width: 110, padding: '4px 8px', fontSize: 11 }}
                                >
                                  <option value="">None</option>
                                  <option value="Headline">Headline</option>
                                  <option value="Body">Body</option>
                                  <option value="Display">Display</option>
                                  <option value="Caption">Caption</option>
                                  <option value="Code">Code</option>
                                </select>
                                <button onClick={() => handleUpdateNote(f.id)} style={{ ...btnStyle(false), padding: '4px 8px', fontSize: 10, color: theme.green }}>save</button>
                                <button onClick={() => setEditingFontId(null)} style={{ ...btnStyle(false), padding: '4px 8px', fontSize: 10 }}>cancel</button>
                              </div>
                            ) : (
                              <>
                                <button
                                  onClick={() => { setEditingFontId(f.id); setEditNote(f.usage_note || ''); }}
                                  style={{ background: 'none', border: 'none', color: theme.textDisabled, cursor: 'pointer', fontSize: 12, padding: '2px 4px' }}
                                  title="Edit usage"
                                >&#9998;</button>
                                <button
                                  onClick={() => handleRemoveFont(f.id)}
                                  style={{ background: 'none', border: 'none', color: theme.textDisabled, cursor: 'pointer', fontSize: 14, padding: '2px 4px' }}
                                  title="Remove from kit"
                                >&times;</button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: theme.textMuted }}>kit not found</div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
